import React, { useEffect } from "react";

import clsx from "clsx";
import { components } from "react-select";
import CreatableSelect from "react-select/creatable";

import { gql } from "@apollo/client";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { GET_LABEL_ASSIGNMENT } from "@gql/label-assignment";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type BulkAddAdObjectLabel,
  type BulkAddAdObjectLabelVariables,
  type BulkRemoveAdObjectLabel,
  type BulkRemoveAdObjectLabelVariables,
} from "@north-beam/nb-client-side";
import { slugify } from "@north-beam/nb-common";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { notifyLabelsCache } from "@shared/notify-labels-cache";
import Spinner from "@shared/spinner";
import { toReactSelectLabel, toReactSelectLabelMulti } from "@utils/index";

import { type Selection } from "..";
import { type TabId } from "../tabs";

import { ButtonGroup } from "./button-group";

interface Props {
  isOpen: boolean;
  selection: Selection;
  activeTab: TabId;
  totalRows: number;
  queryVariables: any;
  onClose: () => void;
  onChange: () => void;
}

const BULK_ADD_AD_OBJECT_LABEL_V2 = gql`
  mutation BulkAddAdObjectLabelV2(
    $selectionMode: String!
    $ids: [ID!]!
    $labelKey: String!
    $labelValue: String!
    $status: String
    $breakdowns: [SalesTableReportV3BreakdownsFilterInput]
    $manualOverride: String
    $search: String
    $granularity: String
  ) {
    bulkAddAdObjectLabelV2(
      selectionMode: $selectionMode
      ids: $ids
      labelKey: $labelKey
      labelValue: $labelValue
      status: $status
      breakdowns: $breakdowns
      manualOverride: $manualOverride
      search: $search
      granularity: $granularity
      manual: true
    )
  }
`;

const BULK_REMOVE_AD_OBJECT_LABEL = gql`
  mutation BulkRemoveAdObjectLabel(
    $selectionMode: String!
    $ids: [ID!]!
    $status: String
    $breakdowns: [SalesTableReportV3BreakdownsFilterInput]
    $manualOverride: String
    $search: String
    $granularity: String
  ) {
    bulkRemoveAdObjectLabel(
      selectionMode: $selectionMode
      ids: $ids
      status: $status
      breakdowns: $breakdowns
      manualOverride: $manualOverride
      search: $search
      granularity: $granularity
    )
  }
`;

export const BulkUpdatingLabelsModal = ({
  isOpen,
  selection,
  totalRows,
  activeTab,
  queryVariables,
  onClose,
  onChange,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [keyInputValue, setKeyInputValue] = React.useState<string | null>(null);
  const [buttonTab, setButtonTab] = React.useState<string>("overwrite");
  const [valueInputValue, setValueInputValue] = React.useState<string | null>(
    null,
  );

  useEffect(() => {
    setButtonTab("overwrite");
    setKeyInputValue(null);
    setValueInputValue(null);
  }, [isOpen]);

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const keyOptions = toReactSelectLabelMulti(
    breakdownConfigs.map((v) => v.key),
  );

  const currentBreakdown = !keyInputValue
    ? undefined
    : breakdownConfigs.find((v) => v.key === keyInputValue);

  const valueOptions = [
    {
      label: "(Reset to label default)",
      value: "(not set)",
    },
    ...((currentBreakdown && currentBreakdown.choices) ?? []).filter(
      (i) => i.value,
    ),
  ];

  const [bulkAddAdObjectLabel, { loading: isMutating }] = useNorthbeamMutation<
    BulkAddAdObjectLabel,
    BulkAddAdObjectLabelVariables
  >(BULK_ADD_AD_OBJECT_LABEL_V2, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_ASSIGNMENT, variables: queryVariables },
    ],
    awaitRefetchQueries: true,
  });

  const selected = toReactSelectLabel(valueInputValue);

  const [bulkRemoveAdObjectLabel, { loading: isRemoving }] =
    useNorthbeamMutation<
      BulkRemoveAdObjectLabel,
      BulkRemoveAdObjectLabelVariables
    >(BULK_REMOVE_AD_OBJECT_LABEL, {
      useCpuIntensive: true,
      refetchQueries: [
        { query: GET_LABEL_ASSIGNMENT, variables: queryVariables },
      ],
      awaitRefetchQueries: true,
    });

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      titleContent={
        <div className="font-bold text-xl">
          Update breakdown labels for (
          {selection.mode === "inclusive"
            ? selection.ids.length
            : totalRows - selection.ids.length}
          ) {activeTab}s
        </div>
      }
      onClose={onClose}
    >
      <div className="px-[1px]">
        <p className="font-light text-gray+1 text-sm w-[575px] mb-9">
          Adsets inherit labels from Campaigns, and Ads inherit labels from
          Adsets. Children always inherit labels defined for the parent.
        </p>
        <div className="mb-4">
          <ButtonGroup
            options={[
              { label: "Manually overwrite labels", value: "overwrite" },
              {
                label: "Reset labels to auto-assigned defaults",
                value: "reset",
              },
            ]}
            value={buttonTab}
            disabled={isRemoving || isMutating}
            onChange={(value) => {
              setKeyInputValue(null);
              setValueInputValue(null);
              setButtonTab(value);
            }}
          />
        </div>
        {buttonTab === "overwrite" && (
          <div className="flex gap-2.5 items-end mt-8 mb-4">
            <div className="flex-1">
              <div className="uppercase font-semibold text-xs mb-1">
                Breakdown
              </div>
              <CreatableSelect
                components={{
                  DropdownIndicator: (props) => (
                    <components.DropdownIndicator {...props}>
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-chevron-down text-interstellar text-sm px-2",
                          {
                            "text-interstellar+2": !props.isDisabled,
                            "text-gray+1": props.isDisabled,
                          },
                        )}
                      />
                    </components.DropdownIndicator>
                  ),
                  ClearIndicator: (props) => (
                    <components.ClearIndicator {...props}>
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-xmark text-interstellar text-sm px-2 mt-[1px]",
                          "hover:cursor-pointer",
                        )}
                      />
                    </components.ClearIndicator>
                  ),
                }}
                classNames={{
                  control: () => "border border-gray rounded-md h-[32px]",
                  input: () => "text-sm",
                  singleValue: () => "text-sm",
                  placeholder: () => "text-sm",
                  indicatorsContainer: () => "h-[32px]",
                  indicatorSeparator: () => "mt-[8px] h-[20px]",
                  dropdownIndicator: () => "h-[32px] px-1",
                  clearIndicator: () => "h-[32px] px-1",
                  menuPortal: () => "z-[9998]",
                  menu: () => "text-sm",
                  option: (props) => {
                    if (props.isSelected) {
                      return "font-semibold text-interstellar bg-cosmic-4";
                    }

                    if (props.isFocused) {
                      return "bg-cosmic-4";
                    }

                    return "";
                  },
                }}
                menuPortalTarget={document.body}
                isClearable
                options={keyOptions}
                isDisabled={isMutating}
                onChange={(value: any) =>
                  setKeyInputValue(value?.value ?? null)
                }
                value={toReactSelectLabel(keyInputValue)}
                placeholder="Enter key..."
              />
            </div>
            <div className="bg-gray-4 h-[38px] w-4 rounded flex items-center justify-center">
              <i className="fa-thin fa-chevron-right text-xxs"></i>
            </div>
            <div className="flex-1">
              <div className="uppercase font-semibold text-xs mb-1">
                Segment
              </div>
              <CreatableSelect
                components={{
                  DropdownIndicator: (props) => (
                    <components.DropdownIndicator {...props}>
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-chevron-down text-interstellar text-sm px-2",
                          {
                            "text-interstellar+2": !props.isDisabled,
                            "text-gray+1": props.isDisabled,
                          },
                        )}
                      />
                    </components.DropdownIndicator>
                  ),
                  Option: (props) => {
                    return (
                      <components.Option
                        className={clsx({
                          " font-semibold":
                            props.label === "(Reset to label default)",
                        })}
                        {...props}
                      ></components.Option>
                    );
                  },
                  ClearIndicator: (props) => (
                    <components.ClearIndicator {...props}>
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-xmark text-interstellar text-sm px-2 mt-[1px]",
                          "hover:cursor-pointer",
                        )}
                      />
                    </components.ClearIndicator>
                  ),
                }}
                classNames={{
                  control: () => "border border-gray rounded-md h-[32px]",
                  input: () => "text-sm",
                  singleValue: () => "text-sm",
                  placeholder: () => "text-sm",
                  indicatorsContainer: () => "h-[32px]",
                  indicatorSeparator: () => "mt-[8px] h-[20px]",
                  dropdownIndicator: () => "h-[32px] px-1",
                  clearIndicator: () => "h-[32px] px-1",
                  menuPortal: () => "z-[9998]",
                  menu: () => "text-sm",
                  option: (props) => {
                    if (props.isSelected) {
                      return "font-semibold text-interstellar bg-cosmic-4";
                    }

                    if (props.isFocused) {
                      return "bg-cosmic-4";
                    }

                    return "";
                  },
                }}
                menuPortalTarget={document.body}
                isClearable
                isDisabled={!keyInputValue || isMutating}
                value={{
                  ...selected,
                  label:
                    selected?.label === "(not set)"
                      ? "(Reset label to default)"
                      : selected?.label,
                }}
                onChange={(value: any) =>
                  setValueInputValue(value?.value ?? null)
                }
                options={valueOptions}
                placeholder="Enter value..."
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t-[1px] border-t-gray-2 pt-5 px-6">
        <TertiaryButton
          disabled={isRemoving || isMutating}
          className="font-bold uppercase"
          onClick={() => {
            onClose();
          }}
        >
          cancel
        </TertiaryButton>
        <PrimaryButton
          disabled={
            buttonTab === "overwrite"
              ? !keyInputValue || !valueInputValue || isMutating
              : isMutating || isRemoving
          }
          className="font-bold flex items-center gap-2 uppercase"
          onClick={async () => {
            if (buttonTab === "overwrite") {
              if (!keyInputValue || !valueInputValue || isMutating) {
                return;
              }

              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Bulk update label assignments",
                data: {
                  pageContext: { ids: selection.ids },
                },
              });

              const mutationVariables = {
                selectionMode: selection.mode,
                ids: selection.ids.map((e) => slugify(JSON.parse(e))),
                labelKey: keyInputValue,
                labelValue: valueInputValue,
                status: queryVariables.status,
                breakdowns: queryVariables.breakdowns,
                manualOverride: queryVariables.manualOverride,
                search: queryVariables.search,
                granularity: queryVariables.granularity,
              };

              await bulkAddAdObjectLabel({ variables: mutationVariables });
            } else {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Bulk remove label assignments",
                data: {
                  pageContext: { ids: selection.ids },
                },
              });

              await bulkRemoveAdObjectLabel({
                variables: {
                  selectionMode: selection.mode,
                  ids: selection.ids,
                  status: queryVariables.status,
                  breakdowns: queryVariables.breakdowns,
                  manualOverride: queryVariables.manualOverride,
                  search: queryVariables.search,
                  granularity: queryVariables.granularity,
                },
              });
            }
            notifyLabelsCache();
            onChange();
            onClose();
          }}
        >
          {(isRemoving || isMutating) && (
            <Spinner colorClassname="text-white-400" />
          )}
          {buttonTab === "overwrite"
            ? "Yes, overwrite labels"
            : "Yes, Reset to defaults"}
        </PrimaryButton>
      </div>
    </Modal>
  );
};
